import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Cloud,
  Copy,
  Check,
  ExternalLink,
  Save,
  Pencil,
  ShieldCheck,
  Eye,
  EyeOff,
  X,
} from "lucide-react";

/**
 * OneDrive Vault Dialog.
 *
 * Both endpoints (`PUT /students/{id}/onedrive` and
 * `POST /students/{id}/onedrive/reveal`) are protected by `require_sudo`
 * on the backend. The axios interceptor pops the master-PIN dialog when
 * needed and transparently retries — no explicit `usePinSecurity` calls
 * are required here.
 */
export default function OneDriveVaultDialog({ open, student, onClose }) {
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState("");
  const [meta, setMeta] = useState({ updated_at: "", updated_by: "" });
  const [hasUrl, setHasUrl] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [folderReady, setFolderReady] = useState(false);
  const [folderHint, setFolderHint] = useState("");
  const [copiedFolder, setCopiedFolder] = useState(false);

  const studentId = student?.id;
  const studentName = student?.name || "";

  const reveal = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setCancelled(false);
    try {
      const { data } = await api.post(`/students/${studentId}/onedrive/reveal`, {});
      setRevealed(data.url || "");
      setMeta({
        updated_at: data.updated_at || "",
        updated_by: data.updated_by || "",
      });
      setHasUrl(!!data.url);
      setEditing(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // No URL configured yet — open the form directly
        setRevealed("");
        setHasUrl(false);
        setEditing(true);
        setDraft("");
      } else if (status === 440 || status === 428) {
        // User cancelled the PIN prompt or session lost. Keep dialog visible
        // with a soft state so they can retry or close.
        setCancelled(true);
      } else {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (open && studentId) {
      // Reset every time we open
      setRevealed("");
      setMeta({ updated_at: "", updated_by: "" });
      setHasUrl(false);
      setEditing(false);
      setDraft("");
      setCopied(false);
      setShowRaw(false);
      setCancelled(false);
      setFolderName("");
      setFolderReady(false);
      setFolderHint("");
      setCopiedFolder(false);
      // Load canonical folder name (public — no PIN needed)
      api.get(`/students/${studentId}/expediente-folder-name`)
        .then(({ data }) => {
          setFolderName(data.folder_name || "");
          setFolderReady(!!data.ready);
          setFolderHint(data.hint || "");
        })
        .catch(() => { /* silent */ });
      reveal();
    }
  }, [open, studentId, reveal]);

  const save = async () => {
    const v = draft.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      toast.error("La URL debe empezar por http:// o https://");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/students/${studentId}/onedrive`, { url: v });
      toast.success("URL de OneDrive guardada");
      // Re-fetch to update meta (updated_at / updated_by) with audit info
      await reveal();
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      toast.success("URL copiada");
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error("No se pudo copiar la URL");
    }
  };

  const openInTab = () => {
    if (!revealed) return;
    window.open(revealed, "_blank", "noopener,noreferrer");
  };

  const mask = (url) => {
    if (!url) return "";
    if (url.length <= 24) return url;
    return url.slice(0, 20) + "…" + url.slice(-8);
  };

  const formattedDate = meta.updated_at
    ? new Date(meta.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
    : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="onedrive-vault-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-blue-500" />
            OneDrive · {studentName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Consulta o edita la URL de la carpeta OneDrive del alumno. Todas las revelaciones y modificaciones quedan registradas en la auditoría.
          </DialogDescription>
        </DialogHeader>

        {/* Canonical folder-name helper — shared across OneDrive & SharePoint (V1.5). */}
        <div
          className={
            "rounded-xl border p-2.5 text-xs " +
            (folderReady
              ? "border-neutral-200 bg-neutral-50"
              : "border-amber-200 bg-amber-50 text-amber-900")
          }
          data-testid="onedrive-folder-name-helper"
        >
          {folderReady ? (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                Nombre canónico
              </span>
              <code className="flex-1 font-mono text-[13px] font-bold text-neutral-900 select-all">
                {folderName}
              </code>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(folderName);
                    setCopiedFolder(true);
                    toast.success("Nombre copiado");
                    setTimeout(() => setCopiedFolder(false), 2500);
                  } catch (_) { /* ignore */ }
                }}
                className="h-7 px-2 rounded-md border border-neutral-300 bg-white hover:bg-neutral-100 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                data-testid="onedrive-folder-name-copy"
                title="Copiar el nombre exacto para crear la carpeta"
              >
                {copiedFolder ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                {copiedFolder ? "Copiado" : "Copiar"}
              </button>
            </div>
          ) : (
            <div>
              {folderHint || "Asigna un código de expediente para generar el nombre canónico."}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" />
            <div className="text-xs text-neutral-500 mt-2">Verificando PIN maestro…</div>
          </div>
        ) : cancelled ? (
          <div className="py-8 text-center space-y-3" data-testid="onedrive-cancelled">
            <ShieldCheck className="h-8 w-8 text-neutral-300 mx-auto" />
            <div className="text-sm text-neutral-600">
              Necesitas validar el <b>PIN maestro</b> para acceder a la carpeta de OneDrive.
            </div>
            <div className="flex gap-2 justify-center">
              <button
                type="button"
                onClick={reveal}
                className="btn-primary !h-9 !text-xs"
                data-testid="onedrive-retry"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Introducir PIN
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost !h-9 !text-xs border border-neutral-200"
              >
                <X className="h-3.5 w-3.5" /> Cerrar
              </button>
            </div>
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-snug">
              <div className="font-semibold mb-0.5">Carpeta OneDrive del alumno</div>
              Pega aquí el enlace a la carpeta compartida en OneDrive. Se guardará con auditoría (cambio y revelaciones quedan registrados).
            </div>
            <input
              type="url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://1drv.ms/f/..."
              autoFocus
              className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
              data-testid="onedrive-url-input"
            />
            <div className="flex gap-2 justify-end">
              {hasUrl && (
                <button
                  type="button"
                  onClick={() => { setEditing(false); setDraft(""); }}
                  className="btn-ghost !h-9 border border-neutral-200"
                >
                  <X className="h-3.5 w-3.5" /> Cancelar
                </button>
              )}
              <button
                type="button"
                onClick={save}
                disabled={saving || !draft.trim()}
                className="btn-primary !h-9"
                data-testid="onedrive-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "Guardando…" : "Guardar URL"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 mb-1 flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                URL revelada · auditoría registrada
              </div>
              <div className="flex items-start gap-2">
                <div
                  className="flex-1 font-mono text-xs break-all text-neutral-800 bg-white border border-neutral-200 rounded-lg px-2 py-1.5"
                  data-testid="onedrive-url-display"
                >
                  {showRaw ? revealed : mask(revealed)}
                </div>
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  className="h-8 w-8 rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50 inline-flex items-center justify-center shrink-0"
                  title={showRaw ? "Ocultar" : "Mostrar completo"}
                  data-testid="onedrive-toggle-raw"
                >
                  {showRaw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formattedDate && (
                <div className="text-[11px] text-neutral-500 mt-1.5">
                  Actualizada: <span className="font-semibold text-neutral-700">{formattedDate}</span>
                  {meta.updated_by && <> · por {meta.updated_by}</>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={copy}
                className="h-11 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="onedrive-copy"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
              <button
                type="button"
                onClick={openInTab}
                className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold inline-flex items-center justify-center gap-1.5"
                data-testid="onedrive-open"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(revealed);
                  setEditing(true);
                }}
                className="h-11 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="onedrive-edit"
              >
                <Pencil className="h-4 w-4" />
                Editar
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
